type Environment = Record<string, string | boolean | undefined>;

const DEPLOYMENT_ENVIRONMENT_IN_HOST = /(?:^|[.-])(dev01|prod01)(?=$|[.-])/i;

export function resolveIndicatorEnvironment(env: Environment, hostname = ""): "dev01" | "local" | null {
  const host = hostname.toLowerCase();
  const hostEnvironment = host.match(DEPLOYMENT_ENVIRONMENT_IN_HOST)?.[1];
  const configured = [env.VITE_ENVIRONMENT, env.VITE_SENTRY_ENVIRONMENT]
    .map((value) => String(value || "").trim().toLowerCase());

  // Production wins over stale development build inputs.
  if (hostEnvironment === "prod01" || configured.includes("prod01")) return null;
  if (["localhost", "127.0.0.1", "::1", "[::1]"].includes(host)) return "local";
  if (hostEnvironment === "dev01" || configured.includes("dev01")) return "dev01";
  return null;
}

export function resolveSentryEnvironment(env: Environment, hostname = ""): string {
  const configured = String(env.VITE_SENTRY_ENVIRONMENT || "").trim();
  if (configured) {
    return configured.toLowerCase();
  }

  const hostEnvironment = hostname.match(DEPLOYMENT_ENVIRONMENT_IN_HOST)?.[1];
  if (hostEnvironment) {
    return hostEnvironment.toLowerCase();
  }

  return (
    String(env.VITE_ENVIRONMENT || env.MODE || "production").trim().toLowerCase() ||
    "production"
  );
}

// Shared with Vite so legacy build inputs become the browser's canonical keys.
export function canonicalEnvironment(env: Record<string, string>): Record<string, string> {
  const result = { ...env };
  for (const keys of [
    ["VITE_HUB_BASE_URL", "VITE_HUB_URL", "HUB_BASE_URL"],
    ["VITE_API_BASE_URL", "API_BASE_URL"],
  ]) {
    const values = keys.map((key) => String(env[key] ?? "").trim().replace(/\/+$/, "")).filter(Boolean);
    if (new Set(values).size > 1) {
      throw new Error(`Conflicting dashboard configuration: ${keys.join(", ")}. Use the canonical ${keys[0]} key.`);
    }
    result[keys[0]] = values[0] ?? "";
  }
  return result;
}

export function parseDashboardEndpoints(env: Environment) {
  const errors: string[] = [];
  const value = (key: string) => String(env[key] ?? "").trim();
  const endpoint = (key: string, raw: string, required = true, hostOnly = false) => {
    if (!raw && !required) return "";
    try {
      // URL.search/hash are empty for bare delimiters that href still retains.
      if (!raw || /[\s\\?#]/.test(raw)) throw new Error();
      const url = new URL(hostOnly && !raw.includes("://") ? `https://${raw}` : raw);
      if (!/^https?:$/.test(url.protocol) || !url.hostname || url.username || url.password || url.search || url.hash ||
          (hostOnly && url.pathname !== "/") || (!hostOnly && !/^https?:\/\//i.test(raw))) {
        throw new Error();
      }
      return (hostOnly && !raw.includes("://") ? url.host : url.href).replace(/\/+$/, "");
    } catch {
      errors.push(`${key} must be ${hostOnly ? "a host[:port] or an HTTP(S) origin" : "an absolute HTTP(S) URL"} without credentials, query, or fragment.`);
      return "";
    }
  };
  const api = endpoint("VITE_API_BASE_URL (or VITE_HUB_BASE_URL)", value("VITE_API_BASE_URL") || value("VITE_HUB_BASE_URL"));
  const hub = endpoint("VITE_HUB_BASE_URL (or VITE_API_BASE_URL)", value("VITE_HUB_BASE_URL") || value("VITE_API_BASE_URL"));
  const auth = endpoint("VITE_AUTH_BASE_URL", value("VITE_AUTH_BASE_URL"));
  const lb = endpoint("VITE_LB_HOST", value("VITE_LB_HOST"), true, true);
  const agent = endpoint("VITE_AGENT_BASE_URL", value("VITE_AGENT_BASE_URL"), false);
  const proxy = value("VITE_USE_PROXY");
  if (proxy && proxy !== "true" && proxy !== "false") errors.push("VITE_USE_PROXY must be true or false.");
  if (proxy === "true") {
    if (env.DEV !== true) errors.push("VITE_USE_PROXY is supported only by the Vite development server.");
    if (api !== hub) errors.push("VITE_API_BASE_URL and VITE_HUB_BASE_URL must match in proxy mode.");
  }
  return { api: proxy === "true" ? "" : api, hub: proxy === "true" ? "" : hub, auth, lb, agent, errors };
}
